/**
 * features/finance/useFinanceData.ts
 * ------------------------------------------------------------------
 * Live finance data — a self-contained hook (not DataContext), exactly
 * as HR uses useHrData. Finance is a gated domain; keeping its
 * subscriptions out of the global context means the academic app pays
 * nothing for it until a finance screen mounts.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  FINANCE_COLLECTIONS, FINANCE_SETTINGS_ID, DEFAULT_FINANCE_SETTINGS,
  type FeeItem, type FeeStructure, type Invoice, type Payment, type DiscountRule, type FinanceSettings,
  type AppliedSiblingDiscount, type FinanceReconciliation, type UnidentifiedPayment,
  type StaffChildrenLink,
} from '@/lib/financeCore';
import type { Adjustment } from '@/lib/adjustments';

interface FinanceData {
  feeItems: FeeItem[];
  feeStructures: FeeStructure[];
  invoices: Invoice[];
  payments: Payment[];
  discountRules: DiscountRule[];
  appliedSiblingDiscounts: AppliedSiblingDiscount[];
  reconciliations: FinanceReconciliation[];
  unidentifiedPayments: UnidentifiedPayment[];
  // Fee special cases
  staffChildren: StaffChildrenLink[];
  /** Credit notes and write-offs. */
  adjustments: Adjustment[];
  settings: FinanceSettings;
  loading: boolean;
  error: string | null;
}

function useCollection<T>(name: string): { rows: T[]; ready: boolean; err: string | null } {
  const [rows, setRows] = useState<T[]>([]);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, name)),
      (snap) => { setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as T)); setReady(true); },
      (e) => { setErr(e.message); setReady(true); },
    );
    return unsub;
  }, [name]);
  return { rows, ready, err };
}

export function useFinanceData(): FinanceData {
  const feeItems = useCollection<FeeItem>(FINANCE_COLLECTIONS.feeItems);
  const feeStructures = useCollection<FeeStructure>(FINANCE_COLLECTIONS.feeStructures);
  const invoices = useCollection<Invoice>(FINANCE_COLLECTIONS.invoices);
  const payments = useCollection<Payment>(FINANCE_COLLECTIONS.payments);
  const discountRules = useCollection<DiscountRule>(FINANCE_COLLECTIONS.discountRules);
  const appliedSibling = useCollection<AppliedSiblingDiscount>(FINANCE_COLLECTIONS.appliedSiblingDiscounts);
  const reconciliations = useCollection<FinanceReconciliation>(FINANCE_COLLECTIONS.reconciliations);
  const unidentified = useCollection<UnidentifiedPayment>(FINANCE_COLLECTIONS.unidentifiedPayments);
  const staffChildren = useCollection<StaffChildrenLink>(FINANCE_COLLECTIONS.staffChildren);
  const adjustments = useCollection<Adjustment>(FINANCE_COLLECTIONS.adjustments);
  const settingsRows = useCollection<FinanceSettings>(FINANCE_COLLECTIONS.settings);

  const settings = useMemo<FinanceSettings>(() => {
    const found = settingsRows.rows.find((s) => s.id === FINANCE_SETTINGS_ID);
    return found ? { ...DEFAULT_FINANCE_SETTINGS, ...found } : DEFAULT_FINANCE_SETTINGS;
  }, [settingsRows.rows]);

  const loading = !feeItems.ready || !feeStructures.ready || !invoices.ready || !payments.ready || !discountRules.ready || !appliedSibling.ready || !reconciliations.ready || !unidentified.ready || !staffChildren.ready || !adjustments.ready || !settingsRows.ready;
  /* `error` deliberately covers only the collections every finance screen
     needs to be correct. A denied read on a special-case collection must not
     put an error banner over Payments or Statements — useCollection reports
     ready on failure, so the module still loads, just with an empty list.
     The same reasoning already applied to sibling discounts, reconciliations
     and unidentified payments. */
  const error = feeItems.err || feeStructures.err || invoices.err || payments.err || discountRules.err || settingsRows.err;

  return {
    feeItems: feeItems.rows,
    feeStructures: feeStructures.rows,
    invoices: invoices.rows,
    payments: payments.rows,
    discountRules: discountRules.rows,
    appliedSiblingDiscounts: appliedSibling.rows,
    reconciliations: reconciliations.rows,
    unidentifiedPayments: unidentified.rows,
    staffChildren: staffChildren.rows,
    adjustments: adjustments.rows,
    settings,
    loading,
    error,
  };
}
